import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { first, mergeMap } from 'rxjs';
import { Product } from '../../../models';
import { EdiInvoice, GungEdiPrice, EdiOrderMessageRelations } from '../../../models/edi';
import { EdiInvoiceDetailConfigService } from '../../../services/edi-invoice-detail-config/edi-invoice-detail-config.service';
import { EdiInvoicesActionConfigService } from '../../../services/edi-invoices-action-config/edi-invoices-action-config.service';
import { EdiService } from '../../../services/edi/edi.service';
import { EdiNavigationService } from '../../../services/edi/edi-navigation.service';
import { ProductService } from '../../../services/products/product.service';
import { EdiRowTable, EdiRowTableType } from '../edi-row-table/edi-row-table.component';
import { DateUtilService } from 'gung-common';
import { BackendFeatureService } from '../../../services/backend-feature.service';
import { EDI_VIEWER_FEATURE_ID } from '../../../services/edi/edi-viewer-compatibility.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'lib-edi-invoice-detail',
    templateUrl: './edi-invoice-detail.component.html',
    styleUrls: ['./edi-invoice-detail.component.css'],
    standalone: false
})
export class EdiInvoiceDetailComponent implements OnInit {
  // Outgoing messages name their ERP order as the supplier order when erpIds is not filled in
  get erpOrderIds(): string[] {
    if (this.invoiceData?.erpIds?.length) {
      return this.invoiceData.erpIds;
    }
    const supplierOrderId = this.invoiceData?.documentReferences?.supplierOrder?.id;
    return supplierOrderId ? [supplierOrderId] : [];
  }

  relations: EdiOrderMessageRelations | null = null;

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected ediService = inject(EdiService);
  public ediNavigationService = inject(EdiNavigationService);
  public configService = inject(EdiInvoiceDetailConfigService);
  public actionConfig = inject(EdiInvoicesActionConfigService);
  protected productService = inject(ProductService);
  public dateUtilService = inject(DateUtilService);
  protected backendFeatureService = inject(BackendFeatureService);
  protected translateService = inject(TranslateService);
  documentViewerEnabled$ = this.backendFeatureService.isActivated(EDI_VIEWER_FEATURE_ID);

  invoiceId = null;
  invoiceData: EdiInvoice;
  orderId: string;
  ediNavigationBaseRoute: string;

  get itemCount(): number {
    return this.invoiceData?.items?.length || 0;
  }

  get payableAmount(): string {
    return this.formatPrice(this.invoiceData?.summary?.totalPayableAmount);
  }

  get hasSummaryCounts(): boolean {
    return this.hasValue(this.invoiceData?.summary?.lineCount) || this.hasValue(this.invoiceData?.summary?.lineItemsCount);
  }

  get hasBookkeepingAmounts(): boolean {
    const summary = this.invoiceData?.summary;
    return [
      summary?.totalPayableAmountBookkeepingCurrency,
      summary?.totalVATBaseAmountBookkeepingCurrency,
      summary?.totalVATAmountBookkeepingCurrency,
      summary?.totalFreightAmountBookkeepingCurrency,
      summary?.totalAdminFeeBookkeepingCurrency,
      summary?.totalRoundingAmountBookkeepingCurrency
    ].some(price => this.hasPrice(price));
  }

  hasValue(value: unknown): boolean {
    return value !== undefined && value !== null;
  }

  hasPrice(price?: GungEdiPrice): boolean {
    return this.hasValue(price?.value);
  }

  currencyFor(price?: GungEdiPrice): string {
    return price?.currency || this.invoiceData?.currency || '';
  }

  formatPrice(price?: GungEdiPrice, fallback = '—'): string {
    if (!this.hasPrice(price)) return fallback;
    const currency = this.currencyFor(price);
    return `${price.value}${currency ? ` ${currency}` : ''}`;
  }

  formatItemIds(item: EdiInvoice): string {
    const ids = item?.ids;
    return [
      ids?.gungId ? `${this.translateService.instant('ID')}: ${ids.gungId}` : '',
      ids?.ean13 ? `${this.translateService.instant('EAN')}: ${ids.ean13}` : '',
      ids?.upca ? `${this.translateService.instant('UPCA')}: ${ids.upca}` : '',
      ids?.buyerId ? `${this.translateService.instant('BUYER_ID')}: ${ids.buyerId}` : ''
    ].filter(Boolean).join('\n');
  }

  itemsColumns: EdiRowTable[] = [
    {
      header: 'IMAGE',
      type: EdiRowTableType.IMAGE,
      getValue: (item: EdiInvoice) => item?.extra?._images
    },
    {
      header: 'ITEM_IDS',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiInvoice) => this.formatItemIds(item),
      customClass: 'formatted'
    },
    {
      header: 'QUANTITY',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiInvoice) => item.quantities.actualQuantity
    },
    {
      header: 'GROSS_UNIT_PRICE',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiInvoice) => this.formatPrice(item.prices?.actualUnitGrossPrice, '')
    },
    {
      header: 'DISCOUNT_PERCENT',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiInvoice) => `${item.prices?.actualDiscountPercent || 0}%`
    },
    {
      header: 'DISCOUNT_AMOUNT',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiInvoice) => this.formatPrice(item.prices?.actualTotalDiscountAmount, '')
    },
    {
      header: 'NET_UNIT_PRICE',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiInvoice) => this.formatPrice(item.prices?.actualUnitNetPrice, '')
    },
    {
      header: 'TOTAL',
      type: EdiRowTableType.TEXT,
      getValue: (item: EdiInvoice) => this.formatPrice(item.prices?.actualTotalNetPrice, '')
    }
  ];

  ngOnInit(): void {
    this.route.params.subscribe(data => {
      this.invoiceId = data.id;

      const urlSegments = this.router.url.split('?')[0].split('/');
      this.ediNavigationBaseRoute = urlSegments.slice(0, -1).join('/');
      this.ediNavigationService.setNavigationCurrentId(this.ediNavigationBaseRoute, this.invoiceId);

      this.ediService
        .getInvoice(this.invoiceId)
        .pipe(
          mergeMap(result => {
            let ids = [];
            if (result?.data?.items?.length > 0) {
              ids = result.data.items.map(i => i.ids.gungId);
            }
            return forkJoin([of(result), ids ? this.productService.getProductsByIds(ids) : of(null)]);
          })
        )
        .subscribe(([invoiceData, products]) => {
          this.invoiceData = invoiceData.data;
          this.ediService
            .getDocumentRelations(this.invoiceData.orderMessageId)
            .pipe(first())
            .subscribe(relations => (this.relations = relations));
          if (products) {
            this.invoiceData.items.forEach(item => {
              item.extra._images = (products as Product[]).find(p => p.id === item.ids.gungId)?.extra.images;
              return item;
            });
          }
        });
    });
  }
}
